import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './ui/LanguageSwitcher';
import { useState } from 'react';
import {
    PlusCircle,
    LogOut,
    Archive,
    User,
    LayoutDashboard,
    CheckSquare,
    ClipboardList,
    Menu,
    X,
    Users
} from 'lucide-react';

export default function Layout() {
    const { user, logout } = useAuth();
    const { t } = useTranslation();
    const location = useLocation();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const isActive = (path) => location.pathname === path;

    const navLinkClass = (path) => `
    flex items-center px-4 py-3 text-sm font-medium transition-all duration-200 rounded-xl mb-2
    ${isActive(path)
            ? 'bg-primary-50 text-primary-700 shadow-sm translate-x-1'
            : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900 hover:translate-x-1'}
  `;

    const closeMobileMenu = () => setIsMobileMenuOpen(false);

    return (
        <div className="min-h-screen bg-surface-50 flex flex-col md:flex-row font-sans text-gray-800">

            {/* Mobile Header */}
            <header className="md:hidden bg-white border-b border-gray-100 p-4 flex items-center justify-between sticky top-0 z-30">
                <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center text-white font-bold shadow-soft">
                        E
                    </div>
                    <span className="text-lg font-bold text-gray-900">{t('app_title')}</span>
                </div>
                <button
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                    {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
            </header>

            {/* Mobile Overlay */}
            {isMobileMenuOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 md:hidden"
                    onClick={closeMobileMenu}
                />
            )}

            {/* Sidebar */}
            <aside className={`
                w-72 bg-white/95 backdrop-blur-xl border-r border-gray-100 
                fixed md:sticky top-0 h-screen z-50 shadow-soft
                flex flex-col
                transition-transform duration-300 ease-in-out
                ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
            `}>
                <div className="h-20 hidden md:flex flex-shrink-0 items-center px-8 border-b border-gray-100/50 bg-gradient-to-r from-white to-transparent">
                    <div className="flex items-center space-x-2">
                        <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center text-white font-bold shadow-lg shadow-primary-500/30">
                            E
                        </div>
                        <span className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-gray-900 to-gray-600">
                            {t('app_title')}
                        </span>
                    </div>
                </div>

                <div className="p-6 flex-1 overflow-y-auto flex flex-col justify-between">
                    <nav>
                        <div className="mb-8">
                            <p className="px-4 text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-4">{t('menu_main')}</p>

                            {/* Tutor Only */}
                            {user?.role === 'tutor' && (
                                <>
                                    <Link to="/" className={navLinkClass('/')} onClick={closeMobileMenu}>
                                        <LayoutDashboard className="w-5 h-5 mr-3 opacity-70" />
                                        {t('menu_my_reports')}
                                    </Link>
                                    <Link to="/reports/new" className={navLinkClass('/reports/new')} onClick={closeMobileMenu}>
                                        <PlusCircle className="w-5 h-5 mr-3 opacity-70" />
                                        {t('menu_new_report')}
                                    </Link>
                                </>
                            )}
                        </div>

                        {(user?.role === 'head' || user?.role === 'vice') && (
                            <div className="mb-8">
                                <p className="px-4 text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-4">{t('menu_management')}</p>

                                <Link to="/dashboard" className={navLinkClass('/dashboard')} onClick={closeMobileMenu}>
                                    <LayoutDashboard className="w-5 h-5 mr-3 opacity-70" />
                                    Дашборд
                                </Link>

                                <Link to="/tutors" className={navLinkClass('/tutors')} onClick={closeMobileMenu}>
                                    <Users className="w-5 h-5 mr-3 opacity-70" />
                                    Тьюторы
                                </Link>

                                {user?.role === 'head' && (
                                    <Link to="/head/approvals" className={navLinkClass('/head/approvals')} onClick={closeMobileMenu}>
                                        <CheckSquare className="w-5 h-5 mr-3 opacity-70" />
                                        {t('menu_approvals')}
                                    </Link>
                                )}

                                {user?.role === 'vice' && (
                                    <Link to="/vice/approvals" className={navLinkClass('/vice/approvals')} onClick={closeMobileMenu}>
                                        <ClipboardList className="w-5 h-5 mr-3 opacity-70" />
                                        {t('menu_final_check')}
                                    </Link>
                                )}
                            </div>
                        )}

                        <div>
                            <p className="px-4 text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-4">{t('menu_system')}</p>
                            <Link to="/archive" className={navLinkClass('/archive')} onClick={closeMobileMenu}>
                                <Archive className="w-5 h-5 mr-3 opacity-70" />
                                {t('menu_archive')}
                            </Link>
                            <Link to="/profile" className={navLinkClass('/profile')} onClick={closeMobileMenu}>
                                <User className="w-5 h-5 mr-3 opacity-70" />
                                {t('menu_profile')}
                            </Link>
                        </div>
                    </nav>

                    <div>
                        {/* Language Switcher */}
                        <div className="mb-6 px-4">
                            <LanguageSwitcher />
                        </div>

                        <div className="bg-surface-100 rounded-xl p-4 border border-gray-200/50">
                            <div className="flex items-center space-x-3 mb-3">
                                <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 font-bold text-xs">
                                    {user?.username?.[0]?.toUpperCase()}
                                </div>
                                <div className="overflow-hidden">
                                    <p className="text-sm font-semibold text-gray-900 truncate">{user?.username || 'User'}</p>
                                    <p className="text-xs text-gray-500 capitalize truncate">{user?.role ? t(`role_${user.role}`) : 'Tutor'}</p>
                                </div>
                            </div>
                            <button
                                onClick={() => {
                                    logout();
                                    closeMobileMenu();
                                }}
                                className="flex w-full items-center justify-center px-4 py-2 text-xs font-semibold text-red-600 bg-white border border-gray-100 hover:bg-red-50 rounded-lg transition-all shadow-sm hover:shadow"
                            >
                                <LogOut className="w-3.5 h-3.5 mr-2" />
                                {t('menu_logout')}
                            </button>
                        </div>
                    </div>
                </div>
            </aside>

            {/* Main Content */}
            <main className="flex-1 p-4 md:p-8 overflow-x-hidden min-h-screen">
                <div className="max-w-6xl mx-auto animate-in fade-in duration-500">
                    <Outlet />
                </div>
            </main>
        </div>
    );
}
